import {
  type ConversationMessageApiModel as ChatMessage,
  type UsedSource,
  ROLE_VALUES,
  type FeedbackValue,
} from "@noa/api-core";
import { createReferenceMap, parseMessageWithReferences, type ReferenceMapValue } from "@noa/api-client";
import { create } from "zustand";
import type { AppStore } from "./app.types";

export const useAppStore = create<AppStore>((set, get) => ({
  sessionId: "",
  selectedMessageId: "",
  messages: [],
  conversationName: "",

  resetStore: () => set({ sessionId: "", selectedMessageId: "", messages: [], conversationName: "" }),

  setConversation: ({ sessionId, messages }: { sessionId: string; messages: ChatMessage[] }) => {
    const state = get();
    // Idempotent: keep the live in-memory state when this conversation is already active
    // (e.g. the seamless redirect right after sending the first message from Home), so an
    // in-flight stream is never clobbered by a redundant hydration.
    if (state.sessionId === sessionId && state.messages.length > 0) return;
    const lastMessageId = messages[messages.length - 1]?.message_id ?? "";
    // No name: this response doesn't carry one, and keeping the previous conversation's would
    // mislabel this one. Consumers fall back to the list row, which does have it.
    set({ sessionId, messages, selectedMessageId: lastMessageId, conversationName: "" });
  },

  setSessionId: (sessionId: string) => set({ sessionId: sessionId }),
  setConversationName: (name: string) => set({ conversationName: name }),
  setSelectedMessageId: (messageId: string) => set({ selectedMessageId: messageId }),

  getFirstUserMessage: () => {
    const messages = get().messages;
    return messages.find(({ role }) => role === ROLE_VALUES.USER) ?? null;
  },
  getLatestMessage: () => get().messages[get().messages.length - 1],
  getMessageById: (messageId: string) => get().messages.find(({ message_id }) => message_id === messageId),
  addMessage: (message: ChatMessage) => set((state) => ({ messages: [...state.messages, message] })),

  addMessageId: (messageId: string) =>
    set((state) => {
      const lastMessage = state.getLatestMessage();
      lastMessage.message_id = messageId;
      return { messages: [...state.messages] };
    }),

  addUsedSourceData: (usedSource: UsedSource) =>
    set((state) => {
      const lastMessage = state.getLatestMessage();
      lastMessage.used_sources = [...lastMessage.used_sources, usedSource];
      return { messages: [...state.messages] };
    }),

  addUsedSourceToMessage: (messageId: string, usedSource: UsedSource) =>
    set((state) => {
      const messageIndex = state.messages.findIndex(
        (msg) => msg.message_id === messageId || (msg.message_id === "" && msg.role === ROLE_VALUES.ASSISTANT)
      );
      if (messageIndex === -1) return state;

      const newMessages = [...state.messages];
      newMessages[messageIndex] = {
        ...newMessages[messageIndex],
        used_sources: [...newMessages[messageIndex].used_sources, usedSource],
      };
      return { messages: newMessages };
    }),

  addToLastMessage: (textToAdd: string) => {
    const lastMessage = get().getLatestMessage();
    lastMessage.content += textToAdd;

    set((state) => ({ messages: [...state.messages] }));
  },

  setMessageFeedback: (messageId: string, feedback: FeedbackValue) => {
    const message = get().getMessageById(messageId);
    if (!message) {
      console.error(`[setMessageFeedback] Message with ID ${messageId} not found`);
      return;
    }

    message.feedback = feedback;
    set((state) => ({ messages: [...state.messages] }));
  },
}));

export const useSessionId = () => useAppStore((state) => state.sessionId);
export const useSetSessionId = () => useAppStore((state) => state.setSessionId);
export const useConversationName = () => useAppStore((state) => state.conversationName);

export const useMessages = () => useAppStore((state) => state.messages);
export const useAddMessage = () => useAppStore((state) => state.addMessage);
export const useAddToLastMessage = () => useAppStore((state) => state.addToLastMessage);

export const useSelectedMessageId = () => useAppStore((state) => state.selectedMessageId);
export const useSetSelectedMessageId = () => useAppStore((state) => state.setSelectedMessageId);

export const useFirstUserMessage = () => useAppStore((state) => state.getFirstUserMessage());
export const useHasFirstUserMessage = (): boolean => useAppStore((state) => !!state.getFirstUserMessage());
export const useSetConversation = () => useAppStore((state) => state.setConversation);
export const useSetMessageFeedback = () => useAppStore((state) => state.setMessageFeedback);

interface UseReferenceDocsResult {
  documents: UsedSource[];
  referenceMap: Map<string, ReferenceMapValue>;
}

export const useReferenceDocs = (): UseReferenceDocsResult => {
  const messages = useMessages();
  const selectedMessageId = useSelectedMessageId();

  if (!messages.length) return { documents: [], referenceMap: new Map() };

  // A message with message_id === "" is currently being streamed (the ID
  // arrives via a separate SSE event after the first chunks). In that case
  // we show its sources immediately instead of waiting for the ID to land.
  const lastMessage = messages[messages.length - 1];
  const hasStreamingMessage = lastMessage.message_id === "";

  const selectedMessage = hasStreamingMessage
    ? lastMessage
    : messages.find(({ message_id }) => message_id === selectedMessageId);
  if (!selectedMessage) return { documents: [], referenceMap: new Map() };

  const { references } = parseMessageWithReferences(selectedMessage.content);
  const referenceMap: Map<string, ReferenceMapValue> = createReferenceMap({ references, chatMessage: selectedMessage });

  const allSources = selectedMessage?.used_sources ?? [];

  // Deduplicate by source_document_name, keeping the best precedence_rank
  const deduplicatedMap = new Map<string, UsedSource>();
  for (const source of allSources) {
    const existing = deduplicatedMap.get(source.source_document_name);
    if (!existing || source.precedence_rank < existing.precedence_rank) {
      deduplicatedMap.set(source.source_document_name, source);
    }
  }

  return {
    documents: [...deduplicatedMap.values()].sort((a, b) => a.precedence_rank - b.precedence_rank),
    referenceMap,
  };
};
